import React, {
    Dispatch,
    FC,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Axios from "axios";
import { flowRight } from "lodash";
import { Container, Content, FormInstance, Header, Heading } from "rsuite";

import constants from "utils/constants";
import i18n from "utils/i18n";

import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";

import { AppDispatch, RootState } from "store/store";

import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";
import withError, { WithErrorProps } from "hocs/withError/withError";

import {
    loadCultures,
    loadCultureSettings,
    loadCurrencies,
    loadSupportedCultures,
} from "api/environments/tenants/culture-settings-api";
import { loadFeatureConfigurations } from "api/environments/tenants/tenant-feature-configurations-api";
import { loadEnrollCache } from "api/environments/tenants/tenant-enrollments-api";

import { getErrorMessage } from "api/defaults";

import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";
import {
    getActiveStep,
    getEnrollCache,
    getEnrollWorkflow,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-selectors";
import {
    setActiveStepData,
    setEnrollCache,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-actions";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import {
    CultureSettings,
    Currency,
} from "store/environments/tenants/culture-settings/culture-settings-types";
import {
    Features,
    OcrConfiguration,
    OcrMethods,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import {
    EnrollCache,
    EnrollStep,
    EnrollWorkflow,
    InitializeSettingsAndFeaturesData,
    StepStatus,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";

import RegionalSettingsForm from "components/Environments/Tenants/LeftMenu/RegionalSettings/Form/Form";
import FeaturesForm from "components/Environments/Tenants/LeftMenu/FeatureConfigurations/Form/Form";

interface Props extends WithErrorProps, WithLoaderProps {
    setIsStepFormValid: Dispatch<SetStateAction<() => boolean>>;
}

const useAppDispatch = () => useDispatch<AppDispatch>();

const Settings: FC<Props> = ({
    setIsStepFormValid,
    showLoader,
    showError,
}: Props) => {
    const cancelTokenSource = useRequestCancellation();

    const regionalSettingsFormRef = useRef<FormInstance<
        Record<string, any>
    > | null>(null);
    const featuresFormRef = useRef<FormInstance<Record<string, any>> | null>(
        null
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [cultureSettings, setCultureSettings] = useState<CultureSettings>(
        {} as CultureSettings
    );
    const [currencies, setCurrencies] = useState<Currency[]>([]);

    const [supportedCultures, setSupportedCultures] = useState<string[]>([]);
    const [cultures, setCultures] = useState<string[]>([]);

    const [features, setFeatures] = useState<Features>({} as Features);

    const [ocrConfigurations, setOcrConfigurations] =
        useState<OcrConfiguration>({
            ocrMethod: OcrMethods.Llm,
        });

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeEnvironmentMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const enrollWorkflow = useSelector(
        (state: RootState): EnrollWorkflow | null => getEnrollWorkflow(state)
    );

    const activeStep = useSelector((state: RootState): EnrollStep | null =>
        getActiveStep(state)
    );

    const enrollCache = useSelector((state: RootState): EnrollCache | null =>
        getEnrollCache(state)
    );

    const disabled = activeStep?.status !== StepStatus.Running;

    const reginalSettingsTitle = i18n.t(
        "tenantConfig.cultureSettingsPage.header"
    );

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([
            fetchCultureSettings(),
            fetchCurrencies(),
            fetchSupportedCultures(),
            fetchCultures(),
            fetchFeatures(),
            fetchOcrConfigurations(),
        ])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setIsStepFormValid(
                    () => () =>
                        regionalSettingsFormRef?.current?.check() || false
                );
                setDataFetched(true);
            });
    };

    const fetchCultureSettings = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadCultureSettings(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        setCultureSettings(payload);

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
                case StepStatus.Success: {
                    const value =
                        (
                            JSON.parse(
                                activeStep.data ?? ""
                            ) as InitializeSettingsAndFeaturesData
                        ).cultureSettings ?? {};

                    setCultureSettings(value);
                }
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchFeatures = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadFeatureConfigurations(
                        activeEnvironmentMoniker,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        payload.vectorVendorRecognitionEnabled = true;

                        setFeatures(payload);

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
                case StepStatus.Success: {
                    const value =
                        (
                            JSON.parse(
                                activeStep.data ?? ""
                            ) as InitializeSettingsAndFeaturesData
                        ).features ?? {};

                    setFeatures(value);
                }
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchCache = async (): Promise<void> => {
        if (enrollCache) {
            return;
        }

        showLoader?.(true);
        showError?.("");

        try {
            const [payload, status] = await loadEnrollCache(
                activeEnvironmentMoniker,
                enrollWorkflow!.id
            );

            if (status === constants.statusCode.OK && payload) {
                dispatch(setEnrollCache(payload));

                return;
            }

            showError?.(getErrorMessage(String(payload), status));
        } catch (e) {
            if (!Axios.isCancel(e)) {
                showError?.(String(e));

                return;
            }
        }

        showLoader?.(true);
    };

    const fetchCurrencies = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadCurrencies(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        setCurrencies(payload);

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchSupportedCultures = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadSupportedCultures(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        setSupportedCultures(payload);

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchCultures = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadCultures(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        setCultures(payload);

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchOcrConfigurations = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    setOcrConfigurations({ ocrMethod: OcrMethods.Llm });

                    return null;
                case StepStatus.Success: {
                    const value =
                        (
                            JSON.parse(
                                activeStep.data ?? ""
                            ) as InitializeSettingsAndFeaturesData
                        ).ocrConfiguration ?? {};

                    setOcrConfigurations(value);
                }
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleCultureSettingsChange = (formValue: Record<string, any>) => {
        setCultureSettings(formValue as CultureSettings);
        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleFeaturesChange = (formValue: Record<string, any>) => {
        const settings = formValue as Features;
        const ocr = formValue as OcrConfiguration;
        if (settings) {
            setFeatures(settings);
        }

        if (ocr) {
            setOcrConfigurations(ocr);
        }

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    useEffect(() => {
        fetchCache();
    }, []);

    useEffect(() => {
        if (activeEnvironmentId && enrollCache?.tenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, enrollCache?.tenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && enrollCache?.tenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        setIsStepFormValid(
            () => () => regionalSettingsFormRef?.current?.check() || false
        );
    }, [setIsStepFormValid, regionalSettingsFormRef]);

    useEffect(() => {
        switch (activeStep?.status) {
            case StepStatus.Running:
                const data: InitializeSettingsAndFeaturesData = {
                    cultureSettings: cultureSettings,
                    features: features,
                    ocrConfiguration: ocrConfigurations,
                };

                dispatch(setActiveStepData(JSON.stringify(data)));
                break;
            case StepStatus.Success: {
                setCultures([cultureSettings.numberAndDateCulture!]);
                setSupportedCultures([cultureSettings.culture!]);
                setCurrencies([
                    {
                        code: cultureSettings.defaultCurrency!,
                        name: cultureSettings.defaultCurrency!,
                    },
                ]);
            }
        }
    }, [cultureSettings, features]);

    return (
        dataFetched && (
            <Container style={{ position: "relative" }} ref={containerRef}>
                <Header style={{ marginBottom: "1rem" }}>
                    <Heading level={3}>{reginalSettingsTitle}</Heading>
                </Header>
                <Content>
                    <RegionalSettingsForm
                        disabled={disabled}
                        formRef={regionalSettingsFormRef}
                        cultureSettings={cultureSettings}
                        currencies={currencies}
                        supportedCultures={supportedCultures}
                        cultures={cultures}
                        onChange={(formValue) =>
                            handleCultureSettingsChange(formValue)
                        }
                    />
                </Content>

                <Content>
                    <FeaturesForm
                        disabled={disabled}
                        formRef={featuresFormRef}
                        containerRef={containerRef}
                        settings={features}
                        ocrConfigurations={ocrConfigurations}
                        onChange={(formValue) =>
                            handleFeaturesChange(formValue)
                        }
                    />
                </Content>
            </Container>
        )
    );
};
const Component = flowRight(withLoader, withError)(Settings);

export default Component;
